import { useState } from 'react';
import { Button, Card, CardBody, Input } from '@nextui-org/react';
import { signOut } from 'common/src/api/auth';
import { getVerifiedTotpFactorId, verifyTotpCode } from 'common/src/api/mfa';
import useBrand from 'common/src/hooks/useBrand';
import useUserStore from 'common/src/stores/useUserStore';
import { useTranslation } from 'common/src/hooks/useTranslation';
import { Alert } from '../ui';

type Props = {
  onVerified: () => void;
};

// Segundo paso del login para usuarios con verificación en dos pasos activa.
const MfaChallenge = ({ onVerified }: Props) => {
  const { t } = useTranslation('security');
  const { assets: brandAssets } = useBrand();
  const { clearUserProfile } = useUserStore();
  const [code, setCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length !== 6) return;

    setVerifying(true);
    setError(null);
    try {
      const factorId = await getVerifiedTotpFactorId();
      if (!factorId) {
        onVerified();
        return;
      }
      await verifyTotpCode(factorId, code);
      onVerified();
    } catch (err) {
      console.error('MFA verification error:', err);
      setError(
        t(
          'El código no es válido o ya expiró. Intenta con el código actual de tu app.'
        )
      );
      setCode('');
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className='flex min-h-screen w-full items-center justify-center bg-background px-4'>
      <Card shadow='sm' className='w-full max-w-md'>
        <CardBody className='flex flex-col gap-6 p-6'>
          <img
            src={brandAssets.fullLogoLight}
            alt={brandAssets.name}
            className='h-8 w-auto self-start dark:hidden'
          />
          <img
            src={brandAssets.fullLogoDark}
            alt={brandAssets.name}
            className='hidden h-8 w-auto self-start dark:block'
          />

          <div className='flex flex-col gap-1'>
            <h1 className='text-lg font-semibold'>
              {t('Verificación en dos pasos')}
            </h1>
            <p className='text-sm text-default-500'>
              {t(
                'Ingresa el código de 6 dígitos que muestra tu app autenticadora.'
              )}
            </p>
          </div>

          {error && (
            <Alert variant='danger' showIcon>
              {error}
            </Alert>
          )}

          <form onSubmit={handleVerify} className='flex flex-col gap-4'>
            <Input
              label={t('Código')}
              value={code}
              onValueChange={(value) =>
                setCode(value.replace(/\D/g, '').slice(0, 6))
              }
              inputMode='numeric'
              pattern='[0-9]*'
              autoComplete='one-time-code'
              autoFocus
              maxLength={6}
            />
            <Button
              type='submit'
              color='primary'
              isLoading={verifying}
              isDisabled={code.length !== 6}
            >
              {t('Verificar')}
            </Button>
            <Button
              variant='light'
              onPress={() => {
                signOut();
                clearUserProfile();
              }}
            >
              {t('Cerrar sesión')}
            </Button>
          </form>
        </CardBody>
      </Card>
    </div>
  );
};

export default MfaChallenge;
